import { type FC, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import { type BaseRenderer, textRenderer } from 'handsontable/renderers';

import { _isEmptyString, _isNil, _isNotBlank, _isNotEmpty, _notNil } from '@/littledash';
import { errorToast, successToast } from '@/helpers';

import type { Animal, AnimalSex, AnimalSexLabel, AnimalSpecies } from '@/model/Animal.model';
import { AnimalSexLabelToCode } from '@/model/Animal.utils';
import type { MetadataField } from '@/model/Metadata.model';
import { MetadataTypes } from '@/model/Metadata.model';

import { MAX_CAGE_POPULATION } from '@/constants/cage';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { featuresSelector } from '@/support/Selectors';
import { createMeta, type HoTMeta } from '@/support/hot';
import type { CellValue } from '@/support/hot.types';
import { StringUtils } from '@/utils/String.utils';
import { useEntitySchemaFieldsByType, useRefreshEntitySchemaOnMount } from '@/utils/entitySchema/useEntitySchema';
import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';

import type { HotTableRef } from '@/components/UI/SpreadSheet/HotTable';
import type { CompatibleCellChange, CompatibleChangeSource } from '@/components/UI/SpreadSheet/Spreadsheet';
import type { SpreadsheetSettings } from '@/components/UI/SpreadSheet/SpreadSheet.model';
import { spreadSheetValidationTooltip } from '@/components/UI/SpreadSheet/SpreadSheet.utils';
import ApiErrorBanner, { ApiErrorType } from '@/components/ApiErrorBanner/ApiErrorBanner';
import type { CageCardCollection } from '@/components/Cages/Show/Cages';
import Loading from '@/components/Loading';
import AddAnimalsTable from '@/components/Colony/Edit/AddAnimalsTable';
import {
  type AddAnimalRow,
  getMetaData,
  getRequiredMetaColumns,
  initAnimalSettings,
  initialAddAnimalData,
} from '@/components/Colony/Colony.utils';

/**
 * The spreadsheet is built for a full page, so its default height leaves a large
 * empty band under a single row once it is inside a modal.
 */
const MODAL_TABLE_HEIGHT = 320;

/**
 * Handsontable keeps this many blank rows at the end of the grid while the cage has room for
 * another animal. They carry the "+" control in place of a row number and are never validated
 * or submitted.
 */
const SPARE_ROWS = 1;

/** Source the rows filled in by the "+" row-header control are written under. */
const ADD_ROW_SOURCE = 'add-row.click';

/** Source the generated name of a row started by typing into the spare row is written under. */
const AUTO_NAME_SOURCE = 'spare-row.name';

/** Matches the names this modal generates, so a renamed row is left out of the numbering. */
const generatedAnimalName = /^Animal (\d+)$/;

/** `_isBlank` for the `unknown` the grid hands over, which would otherwise need a cast to reach. */
const isBlankCellValue = (value: unknown): boolean => _isNil(value) || _isEmptyString(value);

/**
 * Shows the cage on every animal row, leaving the trailing spare row blank.
 *
 * The name is painted rather than written into the row because a row holding nothing but a cage
 * name counts as filled in, which `minSpareRows` would answer with a further spare row.
 */
const renderCageName =
  (cageName: string): BaseRenderer =>
  (hotInstance, td, row, column, prop, _value, cellProperties) => {
    const isSpareRow = row === hotInstance.countRows() - 1 && hotInstance.isEmptyRow(row);
    textRenderer(hotInstance, td, row, column, prop, isSpareRow ? '' : cageName, cellProperties);
  };

const unknownSexSelector = createSelector([featuresSelector], (features) => ({
  unknownSexEnabled: features?.unknown_sex === true,
}));

interface AnimalPostData {
  cage_id?: string;
  name: string;
  dob?: string | null;
  sex?: AnimalSex;
  strain_id?: string;
  metadata?: Array<{ glossary_id: string; value: unknown }>;
}

interface BulkAddSubjectsProps {
  cage: CageCardCollection;
  handleCallback?: (response: { data: Array<Animal> }, cage: CageCardCollection) => void;
  closeModal: () => void;
}

const CAGE_AT_LIMIT_MESSAGE = 'This cage has reached its animal population limit';

const getPopulationSubText = (currentPopulation: number, spacesLeft: number): string | undefined => {
  if (spacesLeft === 0) {
    return CAGE_AT_LIMIT_MESSAGE;
  }
  const population = StringUtils.pluralize(
    currentPopulation
  )`${{ one: 'There is', other: 'There are' }} currently ${currentPopulation} ${{ one: 'animal' }} in this cage.`;
  const capacity = `${StringUtils.pluralize(spacesLeft, 'more space is', 'more spaces are')} available.`;

  return `${population} ${capacity}`;
};

/**
 * Adds animals to a single cage from the cage cards, in both the study and colony views.
 * Renders the same grid as the "Add animals" step of "Add new cages and animals", seeded
 * with one row for the cage in question.
 */
const BulkAddSubjects: FC<BulkAddSubjectsProps> = ({ cage, handleCallback, closeModal }) => {
  const { unknownSexEnabled } = useSelector(unknownSexSelector);
  useRefreshEntitySchemaOnMount();
  const entityFieldRequiredMap = useEntitySchemaFieldsByType('animal');

  const tableRef = useRef<HotTableRef>(null);

  const currentCagePopulation = Math.max(cage.counts?.animals ?? 0, cage.subjects?.data?.length ?? 0);
  const spacesLeft = Math.max(MAX_CAGE_POPULATION - currentCagePopulation, 0);
  const isCageFull = spacesLeft === 0;

  const [data] = useState<Array<AddAnimalRow>>(() =>
    initialAddAnimalData({ name: cage.name, catalog: cage.catalog, counts: { animals: currentCagePopulation } })
  );
  const [glossary, setGlossary] = useState<Array<MetadataField> | null>(null);
  const [metadataAvailableToAdd, setMetadataAvailableToAdd] = useState<Array<MetadataField> | null>(null);
  const [isInvalid, setIsInvalid] = useState(false);
  const [enteredRowCount, setEnteredRowCount] = useState(1);
  const [glossaryLoading, setGlossaryLoading] = useState(true);
  const [glossaryLoadFailed, setGlossaryLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);

  const { response: speciesResponse, loading: speciesLoading } = useApiHook({
    endpoint: 'GET /api/v1/species',
    query: { include: ['strains'] },
    options: {
      invokeOnInit: true,
      onError: { throw: false, capture: true, toast: false, slug: 'bulk-add-subjects-species-load' },
    },
  });

  const species = useMemo<Array<AnimalSpecies>>(
    () => (speciesResponse?.type === 'success' ? (speciesResponse.body.data as unknown as Array<AnimalSpecies>) : []),
    [speciesResponse]
  );

  // An empty glossary is indistinguishable from a failed fetch once it reaches the grid, and a
  // missing required metadata column would let animals be created without values the team marked
  // mandatory. A failure is therefore recorded rather than folded into an empty list.
  useEffect(() => {
    let cancelled = false;
    getMetaData(MetadataTypes.subject_meta)
      .then((metadataResponse) => {
        if (cancelled) {
          return;
        }
        if (metadataResponse?.type === 'success') {
          setGlossary((metadataResponse.body ?? []) as Array<MetadataField>);
        } else {
          setGlossaryLoadFailed(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setGlossaryLoadFailed(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setGlossaryLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const animalFieldsLoadFailed = glossaryLoadFailed || speciesResponse?.type === 'error';

  // Held back until the glossary lands so required metadata is a column from the first render.
  const { settings, unusedMetadata } = useMemo<{
    settings: SpreadsheetSettings | null;
    unusedMetadata: Array<MetadataField>;
  }>(() => {
    if (!_notNil(glossary)) {
      return { settings: null, unusedMetadata: [] };
    }
    const result = initAnimalSettings(
      species,
      true,
      true,
      getRequiredMetaColumns(glossary),
      glossary,
      undefined,
      entityFieldRequiredMap,
      unknownSexEnabled
    );
    const columns = (result.settings.columns ?? []).map((column) =>
      column.data === 'cage_name' ? { ...column, renderer: renderCageName(cage.name) } : column
    );

    return {
      settings: {
        ...result.settings,
        columns,
        height: MODAL_TABLE_HEIGHT,
        minSpareRows: SPARE_ROWS,
      },
      unusedMetadata: result.metadata ?? glossary,
    };
  }, [species, glossary, entityFieldRequiredMap, unknownSexEnabled, cage.name]);

  const exceedsSpacesLeft = enteredRowCount > spacesLeft;
  const everySpaceFilled = enteredRowCount >= spacesLeft;

  // The grid narrows this list as columns are added, so its own choice wins once made.
  const metadataToOffer = metadataAvailableToAdd ?? unusedMetadata;

  /**
   * Handsontable adds and removes rows in place on the array it was handed, so `data` is the
   * live grid contents. The blank spare row carrying the "+" control is dropped here: a row is
   * only ever nameless while it is spare, since every other row fails validation without a name.
   */
  const enteredRows = (): Array<AddAnimalRow> =>
    data.reduce<Array<AddAnimalRow>>((acc, row) => {
      if (_isNotBlank(row?.name)) {
        acc.push(row);
      }
      return acc;
    }, []);

  /**
   * Handsontable edits `data` without React noticing, so the count is refreshed by hand as rows
   * come and go. This is what keeps the Save button in step with the grid.
   */
  const syncEnteredRowCount = (): void => {
    setEnteredRowCount(enteredRows().length);
  };

  /**
   * The highest `Animal <n>` in the grid. Names count up from here rather than from a row's
   * position, so a name is not handed out twice after a row in the middle has been removed.
   */
  const highestAnimalSuffix = (): number =>
    data.reduce((highest, row) => {
      const suffix = generatedAnimalName.exec(row?.name ?? '')?.[1];
      return _notNil(suffix) ? Math.max(highest, Number(suffix)) : highest;
    }, currentCagePopulation);

  const nextAnimalName = (): string => `Animal ${highestAnimalSuffix() + 1}`;

  /**
   * Answers the "+" control by filling the spare row it sits on with the contents of the row
   * above it, so a user entering littermates only has to change what differs. The animal name is
   * the one field that must stay distinct, so it is generated rather than copied.
   */
  const fillSpareRow = (index: number): void => {
    const hot = tableRef.current?.hotInstance;
    if (!_notNil(hot)) {
      return;
    }

    const changes: Array<[number, number, CellValue]> = [];
    for (let column = 0; column < hot.countCols(); column++) {
      const prop = hot.colToProp(column);
      if (prop === 'name') {
        changes.push([index, column, nextAnimalName()]);
      } else if (prop !== 'id' && index >= 1) {
        const value = hot.getDataAtCell(index - 1, column);
        if (_notNil(value)) {
          changes.push([index, column, value]);
        }
      }
    }

    hot.setDataAtCell(changes, ADD_ROW_SOURCE);
  };

  /**
   * Names a row the user has started by typing into the spare row. Only the "+" control generates
   * a name, and an animal needs one: without it the row fails validation and is left out of the
   * payload.
   */
  const nameStartedRows = (hot: NonNullable<HotTableRef['hotInstance']>, rows: Array<number>): void => {
    // `propToCol` hands the prop straight back when the grid has no column for it, so the answer
    // is only an index once it is a number.
    const nameColumn = hot.propToCol('name');
    if (typeof nameColumn !== 'number' || nameColumn < 0) {
      return;
    }

    let suffix = highestAnimalSuffix();
    const changes = rows.reduce<Array<[number, number, CellValue]>>((acc, row) => {
      if (!hot.isEmptyRow(row) && isBlankCellValue(hot.getDataAtCell(row, nameColumn))) {
        suffix += 1;
        acc.push([row, nameColumn, `Animal ${suffix}`]);
      }
      return acc;
    }, []);

    if (_isNotEmpty(changes)) {
      hot.setDataAtCell(changes, AUTO_NAME_SOURCE);
    }
  };

  /**
   * Refreshes the row count for every edit, including typing into the spare row, which is how a
   * new animal enters the grid without a row ever being created.
   *
   * Rows filled in by the "+" control are validated here rather than as they are written:
   * Handsontable only validates the cells it was handed, leaving the ones the row above had
   * nothing for unchecked, so the whole row is put through again once the copy lands.
   *
   * A row whose name was just cleared is left nameless, so clearing a name to retype it is not
   * answered by putting a generated one back. A paste that clears the name while filling the rest
   * of the row is still named, since a nameless row never reaches the payload.
   */
  const handleAfterChange = (changes: Array<CompatibleCellChange> | null, source: CompatibleChangeSource): void => {
    if (!_notNil(changes)) {
      return;
    }
    syncEnteredRowCount();

    const hot = tableRef.current?.hotInstance;
    if (!_notNil(hot) || source === AUTO_NAME_SOURCE) {
      return;
    }

    const changedRows = new Set<number>();
    const rowsWithClearedNames = new Set<number>();
    const rowsWithFilledCells = new Set<number>();
    changes.forEach(([row, prop, , newValue]) => {
      changedRows.add(row);
      if (prop === 'name') {
        if (isBlankCellValue(newValue)) {
          rowsWithClearedNames.add(row);
        }
      } else if (!isBlankCellValue(newValue)) {
        rowsWithFilledCells.add(row);
      }
    });

    if (source === ADD_ROW_SOURCE) {
      hot.validateRows([...changedRows]);
      return;
    }

    nameStartedRows(
      hot,
      [...changedRows].filter((row) => !rowsWithClearedNames.has(row) || rowsWithFilledCells.has(row))
    );
  };

  const { invoke: postAnimals, response: postResponse } = useApiHook({
    endpoint: 'POST /api/v1/animals',
    options: {
      invokeOnInit: false,
      onError: { throw: false, capture: true, toast: false, slug: 'bulk-add-subjects-create' },
    },
  });

  /**
   * The create endpoint keys metadata by glossary api_id while the grid stores values by
   * encoded column title, so values are resolved against the full glossary rather than the
   * "still available to add" subset.
   */
  const buildMetadataPayload = (meta: AddAnimalRow['meta']): Array<{ glossary_id: string; value: unknown }> => {
    if (!_isNotEmpty(meta) || !_notNil(settings) || !_notNil(settings.columns)) {
      return [];
    }
    const glossaryIdToApiId = (glossary ?? []).reduce<Record<string, string>>((acc, field) => {
      if (_notNil(field.id) && _notNil(field.api_id)) {
        acc[String(field.id)] = field.api_id;
      }
      return acc;
    }, {});

    return (createMeta(meta, settings.columns) as Array<HoTMeta>).reduce<
      Array<{ glossary_id: string; value: unknown }>
    >((acc, entry) => {
      if (_notNil(entry.glossary_id)) {
        acc.push({
          glossary_id: glossaryIdToApiId[String(entry.glossary_id)] ?? String(entry.glossary_id),
          value: entry.value,
        });
      }
      return acc;
    }, []);
  };

  const mapAnimalPostData = (rows: Array<AddAnimalRow>): Array<AnimalPostData> =>
    rows.reduce<Array<AnimalPostData>>((acc, { name, sex, dob, species: animalSpecies, strain, meta }) => {
      const animal: AnimalPostData = {
        cage_id: cage.api_id,
        name,
        dob: dob ?? null,
      };

      if (_notNil(sex)) {
        animal.sex = AnimalSexLabelToCode[sex as AnimalSexLabel];
      }

      if (_notNil(animalSpecies)) {
        const matchingSpecies = species.find((s) => s.name === animalSpecies);
        const matchingStrain = matchingSpecies?.strains?.find((s) => s.name === strain);
        if (_notNil(matchingStrain?.api_id)) {
          animal.strain_id = matchingStrain.api_id;
        }
      }

      const metadata = buildMetadataPayload(meta);
      if (_isNotEmpty(metadata)) {
        animal.metadata = metadata;
      }

      acc.push(animal);
      return acc;
    }, []);

  const saveAnimals = async () => {
    setSaving(true);
    const animals = mapAnimalPostData(enteredRows());
    const result = await postAnimals({ body: { animals } }).catch(() => undefined);
    setSaving(false);

    if (result?.type !== 'success') {
      errorToast('Could not create animals');
      return;
    }

    successToast(`Created ${StringUtils.pluralize(animals.length, 'animal')} in ${cage.name}`);
    // The endpoint returns the created animals as a bare array. Its generated shape differs from
    // the hand-written `Animal` the cage cards are built from only in field nullability.
    handleCallback?.({ data: result.body as unknown as Array<Animal> }, cage);
  };

  // The spare row counts towards the spaces left, so Handsontable stops handing one back once
  // the last of them is filled in.
  const maxRows = spacesLeft;

  const submitDisabled =
    saving ||
    isInvalid ||
    exceedsSpacesLeft ||
    isCageFull ||
    animalFieldsLoadFailed ||
    enteredRowCount === 0 ||
    _isNil(settings);

  const submitTooltip = (): string | undefined => {
    if (isCageFull) {
      return CAGE_AT_LIMIT_MESSAGE;
    }
    if (exceedsSpacesLeft) {
      return `Only ${StringUtils.pluralize(spacesLeft, 'more animal')} can be added to this cage`;
    }
    if (isInvalid) {
      return spreadSheetValidationTooltip;
    }
    return undefined;
  };

  return (
    <ModalContainer size="wide" className="bg-white" testKey="bulk-add-subjects">
      <ModalHeader
        title={`Add animals to ${cage.name}`}
        subText={getPopulationSubText(currentCagePopulation, spacesLeft)}
        // Drops the default `measure-wide`, which caps the sub heading at 34em and wraps it.
        subTextClasses="f6 lh-copy mt2"
        closeModal={closeModal}
        closeDisabled={saving}
        className="pa3 bb b--moon-gray w-100"
      />

      {postResponse?.type === 'error' && <ApiErrorBanner className="ma3" error={postResponse.error} />}

      {!isCageFull &&
        (animalFieldsLoadFailed ? (
          <ApiErrorBanner
            className="ma3"
            errorType={ApiErrorType.FETCH}
            text="The animal fields for this cage could not be loaded, so animals cannot be added right now. Please close this window and try again."
          />
        ) : speciesLoading || glossaryLoading || !_notNil(settings) ? (
          <div style={{ height: MODAL_TABLE_HEIGHT }}>
            <Loading txt="Loading animal fields..." />
          </div>
        ) : (
          <div className="ow-spreadsheet-styles pa3">
            <AddAnimalsTable
              data={data as unknown as Array<Record<string, unknown>>}
              settings={settings}
              innerRef={tableRef}
              metadata={metadataToOffer}
              setMetadata={setMetadataAvailableToAdd}
              setInvalid={setIsInvalid}
              afterChange={handleAfterChange}
              afterRemoveRow={syncEnteredRowCount}
              maxRows={maxRows}
              minSpareRows={everySpaceFilled ? 0 : SPARE_ROWS}
              enableRowHeaderControls
              onRowHeaderAddRow={fillSpareRow}
            />
          </div>
        ))}

      <ModalActions
        className="pa3 bt b--moon-gray w-100"
        onSubmit={saveAnimals}
        onCancel={closeModal}
        submitBtnText="Save"
        cancelBtnText="Cancel"
        submitButtonProps={{ loading: saving, disabled: submitDisabled, tooltip: submitTooltip() }}
        cancelButtonProps={{ disabled: saving }}
      />
    </ModalContainer>
  );
};

export default BulkAddSubjects;
